import React, { useState, useEffect } from 'react';
import Tesseract from 'tesseract.js';
import apiService from '../services/apiService';
import styled from "styled-components";
import MeaningPopup from './MeaningPopup';
import { Volume2 } from 'lucide-react';
import { useSmartTTS } from '../hooks/useSmartTTS'; // Import smart hook
import { useLocation } from 'react-router-dom'; // Import useLocation
import AnalyzedResult from './AnalyzedResult';

// 모바일 최적화 및 다크모드 스타일
const SectionCard = styled.section`
  background: #141414;
  border-radius: 16px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.5);
  border: 1px solid #222;
  color: #e5e5e5;
  padding: 20px 12px;
  margin-bottom: 20px;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
  @media (max-width: 600px) {
    padding: 12px 2px;
    border-radius: 12px;
  }
`;

const Title = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 12px;
  color: #e50914;
  letter-spacing: 1px;
`;

const Description = styled.p`
  color: #b3b3b3;
  margin-bottom: 18px;
  font-size: 0.98rem;
  line-height: 1.5;
`;

const InputArea = styled.div`
  margin-bottom: 18px;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  margin-bottom: 12px;
  font-size: 1rem;
  border-radius: 8px;
  border: 1.5px solid #333;
  padding: 12px;
  background: #222;
  color: #e5e5e5;
  resize: vertical;
  box-sizing: border-box;
  transition: border 0.2s;
  &:focus {
    border: 1.5px solid #e50914;
    outline: none;
    background: #181818;
  }
`;

const StyledButton = styled.button`
  background: #e50914;
  color: #fff;
  font-weight: 700;
  border: none;
  border-radius: 8px;
  padding: 12px 28px;
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 18px;
  box-shadow: 0 2px 8px rgba(229,9,20,0.12);
  transition: background 0.2s, transform 0.1s;
  &:active {
    transform: scale(0.98);
  }
  &:disabled {
    background: #333;
    color: #888;
    cursor: not-allowed;
  }
`;

const ClearButton = styled.button`
  background: transparent;
  color: #b3b3b3;
  font-weight: 600;
  border: 1px solid #555;
  border-radius: 8px;
  padding: 11px 20px; /* Reduced by 1px to account for border and match height */
  font-size: 1rem;
  cursor: pointer;
  margin-left: 10px;
  margin-bottom: 18px;
  transition: all 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border-color: #777;
  }
`;

// End of styled components
// Removed unused components (SentenceTitle, Label, WordTag, MatchTag, CompleteText)

function SentenceExtractor(props) {
  const location = useLocation(); // Hook to get passed state
  const [inputText, setInputText] = useState('');
  const [results, setResults] = useState([]);
  const [popup, setPopup] = useState(null);
  const [wordInfo, setWordInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [loadingWord, setLoadingWord] = useState(false);
  const { speak } = useSmartTTS();

  // Check for passed state from AI Generator or Props
  useEffect(() => {
    // Priority: Props > Location State
    const initialText = props.initialText || (location.state && location.state.initialText);
    const autoAnalyze = props.autoAnalyze || (location.state && location.state.autoAnalyze);

    if (initialText) {
      setInputText(initialText);
      if (autoAnalyze) {
        // Use setTimeout to ensure state update of inputText is processed
        setTimeout(() => {
          document.getElementById('extract-btn')?.click();
        }, 100);
      }
    }
  }, [location.state, props.initialText, props.autoAnalyze]);

  // Check guest session expiry
  React.useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user && user.isGuest) {
      const lastLogin = localStorage.getItem('guestLastLogin');
      const today = new Date().toDateString();

      let expired = false;
      if (!lastLogin) {
        expired = true;
      } else {
        const lastLoginDate = new Date(lastLogin).toDateString();
        if (lastLoginDate !== today) {
          expired = true;
        }
      }

      if (expired) {
        alert('비회원 사용기간(1일)이 만료되었습니다. 다시 로그인해주세요.');
        localStorage.removeItem('user');
        localStorage.removeItem('guestLastLogin');
        window.location.href = '/login';
      }
    }
  }, []);

  // 이미지 리사이징 헬퍼 함수
  const resizeImage = (file, maxDimension = 1200) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxDimension) {
              height *= maxDimension / width;
              width = maxDimension;
            }
          } else {
            if (height > maxDimension) {
              width *= maxDimension / height;
              height = maxDimension;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.9));
        };
        img.src = e.target.result;
      };
    });
  };

  // 이미지에서 텍스트 추출
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setOcrLoading(true);
    try {
      // 1. 이미지 리사이징 (속도 최적화)
      const resizedImage = await resizeImage(file);

      // 2. OCR 수행
      const { data: { text } } = await Tesseract.recognize(resizedImage, 'eng+kor');
      setInputText(text);
    } catch (err) {
      console.error(err);
      alert('이미지에서 텍스트 추출 실패');
    }
    setOcrLoading(false);
    // 입력창 초기화 (동일 파일 재선택 가능하도록)
    e.target.value = '';
  };

  const splitSentences = (text) => {
    return text
      .replace(/\r?\n/g, ' ')
      .match(/[^.!?]+[.!?]?/g)
      ?.map(s => s.trim())
      .filter(s => s.length > 0) || [];
  };

  const handleClear = () => {
    if (!inputText && results.length === 0) return;
    setInputText('');
    setResults([]);
  };

  const handleExtract = async () => {
    setLoading(true);
    setResults([]); // Reset results
    const sentences = splitSentences(inputText);
    const accumulatedResults = [];
    const userId = JSON.parse(localStorage.getItem('user'))?.id || null;
    const feedId = props.feedId || (location.state && location.state.feedId); // Pass feedId if available

    try {
      // 1. Check Cache/History First
      let usedCache = false;
      console.log(`[Extract] Checking cache for userId=${userId}, feedId=${feedId}`);

      if (userId && inputText.trim()) {
        try {
          const checkRes = await apiService.checkSentenceHistory(userId, inputText.trim(), feedId);
          console.log('[Extract] Cache Check Result:', checkRes);

          if (checkRes.found && checkRes.data) {
            // Cache Hit! Use existing data
            console.log('Cache Hit: Using existing analysis history (Source:', checkRes.source, ')');
            setResults(checkRes.data);

            // [NEW] If sourced from 'feed_analysis' (Pre-calc), adopt it to User History
            if (checkRes.source === 'pre_analysis') {
              console.log("Adopting Pre-Analysis to User History...");
              // Fire and forget save (or await if critical, but user sees data immediately)
              apiService.saveUserSentence(userId, checkRes.data, inputText, { feedId }).catch(e => console.error("History Adopt Error", e));
            }

            setLoading(false);
            return; // Stop here, no need to call AI
          }
        } catch (e) {
          console.warn('Cache check failed, proceeding to AI', e);
        }
      }

      // 2. Cache Miss: Proceed with AI Generation
      // [Client-Side Streaming Implementation]
      // 서버 버퍼링 문제를 근본적으로 해결하기 위해,
      // 프론트엔드에서 문장별로 '개별 요청'을 보냅니다. (가장 확실한 방법)
      for (const sentence of sentences) {
        try {
          // 개별 문장 번역 요청 (기존의 일반 API 재사용)
          const response = await apiService.sentenceMatch([sentence]);

          if (response.data && response.data.length > 0) {
            const newItem = response.data[0];
            accumulatedResults.push(newItem);

            // 화면 즉시 갱신
            setResults(prev => [...prev, newItem]);
          }
        } catch (innerErr) {
          console.error(`Sentence processing failed: ${sentence}`, innerErr);
          // 실패한 문장은 건너뛰거나 에러 표시 후 계속 진행
        }
      }

      // 모든 처리가 끝난 후 전체 저장
      if (accumulatedResults.length > 0) {
        const savedData = await apiService.saveUserSentence(userId, accumulatedResults, inputText, { feedId });

        // Notify parent (e.g., FeedPage)
        if (props.onAnalysisComplete) {
          props.onAnalysisComplete({
            feedId: feedId,
            data: accumulatedResults,
            docId: savedData?.data?.id // Ensure we pass the new docId
          });
        }
      }

    } catch (err) {
      console.error(err);
      alert('번역 과정 중 오류가 발생했습니다.');
    }
    setLoading(false);
  };

  // ChatGPT 단어 의미 요청
  const handleWordClick = async (word) => {
    if (!popup) return;
    setWordInfo({ word, meanings: [] });
    setLoadingWord(true);
    const userId = JSON.parse(localStorage.getItem('user'))?.id;

    try {
      const gptRes = await apiService.wordMeaning({
        source: popup.source,
        target: popup.target,
        word,
        userId
      });
      const meanings = gptRes.data;
      setWordInfo({ word, meanings });

      // Save to history
      if (userId) {
        try {
          // Extract up to 3 meanings to array
          const extractedMeanings = meanings.slice(0, 3).map(m => ({
            meaning: m.의미1 || m.의미2 || m.의미3 || m.meaning || '',
            example: m.예문1 || m.예문2 || m.예문3 || m.example || ''
          }));

          const saveRes = await apiService.saveWordHistory(userId, {
            word,
            meanings: extractedMeanings, // Send extracted array
            // Fallback legacy fields (optional as backend handles it, but good for safety)
            translation: extractedMeanings[0]?.meaning || '',
            exampleSentence: extractedMeanings[0]?.example || ''
          });

          // Update state with ID and isFavorite from backend to enable favorite toggling
          setWordInfo(prev => ({
            ...prev,
            id: saveRes.id,
            isFavorite: saveRes.isFavorite
          }));

        } catch (err) {
          console.error('History save failed', err);
        }
      }

    } catch (err) {
      console.error(err);
      setWordInfo({ word, meanings: [] });
    }
    setLoadingWord(false);
  };

  return (
    <div className="App" style={{ background: "#141414", minHeight: "100vh", padding: "0" }}>
      {!props.isModal && (
        <header style={{ background: "#181818", color: "#e50914", padding: "18px 0 10px 0", fontSize: "2rem", fontWeight: 900, letterSpacing: "2px", textAlign: "center", borderBottom: "2px solid #222" }}>
          NUREUM VOCA
        </header>
      )}
      <SectionCard>
        <Title>문장 분석</Title>
        <Description>
          텍스트를 복사해서 붙이거나 직접 입력한 후, 추출하기 버튼을 누르세요.<br />
          이미지는 문장영역을 정확하게 잘라서 올려 주세요.<br />
          문장별로 번역 결과가 아래에 표시됩니다.<br />
          의미단위별로 클릭하면 매칭된 번역/원문을 팝업으로 볼 수 있습니다.
        </Description>
        <InputArea>
          <input
            type="file"
            id="file-upload"
            accept="image/*"
            onChange={handleImageUpload}
            disabled={ocrLoading}
            style={{ display: 'none' }}
          />
          <label htmlFor="file-upload">
            <div
              style={{
                background: '#333',
                color: '#fff',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'inline-block',
                fontSize: '0.95rem',
                border: '1px solid #444',
                marginRight: '12px'
              }}
            >
              이미지 선택
            </div>
          </label>
          {ocrLoading && <span style={{ color: "#e50914", fontSize: "0.95rem" }}>이미지에서 텍스트 추출 중...</span>}
        </InputArea>
        <StyledTextarea
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          placeholder="텍스트를 입력하거나 이미지를 업로드하세요"
          rows={5}
        />
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <StyledButton
            id="extract-btn"
            onClick={handleExtract}
            disabled={loading || !inputText}
            style={{ marginBottom: 18 }}
          >
            {loading ? '추출 중...' : '문장 추출 및 번역'}
          </StyledButton>
          <ClearButton onClick={handleClear} title="입력 초기화">
            초기화
          </ClearButton>
        </div>
        <div>
          {loading && results.length === 0 && (
            <div style={{
              textAlign: "center",
              padding: "40px 0",
              color: "#b3b3b3",
              fontSize: "1rem",
              lineHeight: "1.6"
            }}>
              Deeple AI 에게 번역을 요청 중입니다.<br />
              서비스 사정에 따라 시간이 걸릴 수 있습니다.
            </div>
          )}
          {results.map((item, idx) => (
            <AnalyzedResult
              key={idx}
              item={item}
              idx={idx}
              speak={speak}
              onWordClick={(source, target) => {
                setPopup({ source, target });
                setWordInfo(null);
              }}
            />
          ))}
        </div>
        {popup && (
          <MeaningPopup
            source={popup.source}
            target={popup.target}
            onClose={() => { setPopup(null); setWordInfo(null); setLoadingWord(false); }}
            wordInfo={wordInfo}
            onWordClick={handleWordClick}
            loadingWord={loadingWord}
            onUpdateWordInfo={setWordInfo}
          />
        )}
      </SectionCard>
    </div>
  );
}

export default SentenceExtractor;